import { defineStore } from "pinia";
import { Campaign } from "@/types/campaign";
import { APIClient, Response } from "@/utils/httpClient";
import router from "@/router";
import { authStore } from "./authStore";
import {
	buildInitialCampaignRequestPayload,
	type InitialCampaignSource,
} from "@/utils/campaignSource";

const buildErrorRoute = (message: string): string => {
	const params = new URLSearchParams({ message });
	const campaignId = authStore().getCampaignID;
	if (campaignId) {
		params.set("campaign", campaignId);
	}
	return `/error?${params.toString()}`;
};

interface ICampaignStoreState {
	campaign: Campaign | null;
	deviceId: string | null;
}

export const campaignStore = defineStore("campaignStore", {
	persist: true,
	state: () =>
	({
		campaign: null,
		deviceId: null,
	} as ICampaignStoreState),
	getters: {
		getItem(state: ICampaignStoreState) {
			return state.campaign;
		},
		getDeviceId(state: ICampaignStoreState) {
			return state.deviceId;
		},
	},
	actions: {
		async getInitialCampaignData(campaignSource: InitialCampaignSource) {
			const endpoint = `/campaigns/initial`;
			const data = buildInitialCampaignRequestPayload(campaignSource);
			try {
				const response = await APIClient.post(endpoint, data, {
					headers: { auth_type: "manager" },
				});
				if (response.success) {
					console.log(response.data);
					return response.data;
				}
			} catch (error: any) {
				console.error(error);
				if (error instanceof Error) {
					router.push(buildErrorRoute(error.message));
				} else {
					router.push(buildErrorRoute("Unknown error"));
				}
			}
		},
		async getCampaignFromPath(path: string): Promise<Response> {
			const endpoint = `/campaigns/path/${path}`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					this.campaign = response.data;
					return response;
				}
			} catch (error: any) {
				console.error(error);
				if (error instanceof Error) {
					router.push(buildErrorRoute(error.message));
				} else {
					router.push(buildErrorRoute("Unknown error"));
				}
			}
			return { success: false, data: null, message: "No campaign found" };
		},
		setCampaign(item: Campaign): void {
			this.campaign = item;
		},
		setDeviceId(id: string): void {
			this.deviceId = id;
		},
		async getCampaign() {
			const authState = authStore();
			const campaignId = authState.campaignId;
			const endpoint = `/campaigns/${campaignId}`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					this.campaign = response.data;
					return response.data;
				} else {
					if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-ENDED"
					) {
						router.replace(`/${campaignId}/campaign-over`);
					} else if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-PAUSED"
					) {
						router.replace(`/${campaignId}/campaign-paused`);
					} else {
						return null;
					}
				}
			} catch (error: any) {
				console.error(error);
				if (error instanceof Error) {
					router.push(buildErrorRoute(error.message));
				} else {
					router.push(buildErrorRoute("Unknown error"));
				}
			}
		},
		async shareCampaign() {
			const authState = authStore();
			const campaignId = authState.campaignId;
			const userId = authState.userId;
			const endpoint = `/campaigns/${campaignId}/users/${userId}/shared`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					console.log("Campaign Shared");
				} else {
					if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-ENDED"
					) {
						router.replace(`/${campaignId}/campaign-over`);
					} else if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-PAUSED"
					) {
						router.replace(`/${campaignId}/campaign-paused`);
					}
				}
			} catch (error) {
				console.error(error);
			}
		},
	},
});
